import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../firebase/AuthProvider";
import {
  EmployeeMilestone,
  getEmployeeMilestones,
} from "../services/EmployeeService/EmployeeMilestoneService";

export interface ResolvedEmployeeMilestone extends EmployeeMilestone {
  years?: number;
}

export interface EmployeeCelebration {
  userId: string;
  displayName: string;
  birthday?: ResolvedEmployeeMilestone;
  anniversary?: ResolvedEmployeeMilestone;
  primaryType: "BIRTHDAY" | "WORK_ANNIVERSARY" | "MULTIPLE";
  compactLabel: string;
  fullLabel: string;
  accessibleLabel: string;
}

interface EmployeeMilestoneContextType {
  milestones: EmployeeMilestone[];
  isLoading: boolean;
  error: string | null;
  refreshMilestones: () => Promise<void>;
  getMilestonesForUser: (
    userId: string,
    date?: Date,
  ) => ResolvedEmployeeMilestone[];
  getCelebrationForUser: (
    userId: string,
    date?: Date,
  ) => EmployeeCelebration | null;
}

const EmployeeMilestoneContext =
  createContext<EmployeeMilestoneContextType | null>(null);

const isLeapYear = (year: number): boolean =>
  year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const projectedDayForYear = (
  month: number,
  day: number,
  year: number,
): { month: number; day: number } => {
  if (month === 2 && day === 29 && !isLeapYear(year)) {
    return { month: 2, day: 28 };
  }

  return { month, day };
};

const matchesDate = (
  milestone: EmployeeMilestone,
  date: Date,
): boolean => {
  const projected = projectedDayForYear(
    milestone.month,
    milestone.day,
    date.getFullYear(),
  );

  return (
    projected.month === date.getMonth() + 1 &&
    projected.day === date.getDate()
  );
};

const resolveMilestone = (
  milestone: EmployeeMilestone,
  date: Date,
): ResolvedEmployeeMilestone | null => {
  if (!matchesDate(milestone, date)) return null;

  if (milestone.type === "WORK_ANNIVERSARY") {
    if (!milestone.startYear) return null;

    const years = date.getFullYear() - milestone.startYear;

    // Do not celebrate the hire date as a "0-year anniversary".
    if (years < 1) return null;

    return {
      ...milestone,
      years,
    };
  }

  return { ...milestone };
};

export const EmployeeMilestoneProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { currentUser } = useAuth();

  const [milestones, setMilestones] = useState<EmployeeMilestone[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeRequestRef = useRef(0);

  const milestonesByUser = useMemo(() => {
    const index = new Map<string, EmployeeMilestone[]>();

    milestones.forEach((milestone) => {
      const existing = index.get(milestone.userId) ?? [];
      existing.push(milestone);
      index.set(milestone.userId, existing);
    });

    return index;
  }, [milestones]);

  const refreshMilestones = useCallback(async () => {
    if (!currentUser) {
      setMilestones([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    const requestId = ++activeRequestRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const token = await currentUser.getIdToken();
      const data = await getEmployeeMilestones(token);

      if (requestId !== activeRequestRef.current) return;
      setMilestones(data);
    } catch (err) {
      if (requestId !== activeRequestRef.current) return;

      const message =
        err instanceof Error
          ? err.message
          : "Failed to load employee milestones.";

      console.error("[EMPLOYEE-MILESTONES] Failed to load milestones:", err);
      setMilestones([]);
      setError(message);
    } finally {
      if (requestId === activeRequestRef.current) {
        setIsLoading(false);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      activeRequestRef.current += 1;
      setMilestones([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    void refreshMilestones();

    return () => {
      activeRequestRef.current += 1;
    };
  }, [currentUser, refreshMilestones]);

  const getMilestonesForUser = useCallback(
    (
      userId: string,
      date: Date = new Date(),
    ): ResolvedEmployeeMilestone[] => {
      if (!userId) return [];

      return (milestonesByUser.get(userId) ?? [])
        .map((milestone) => resolveMilestone(milestone, date))
        .filter(
          (milestone): milestone is ResolvedEmployeeMilestone =>
            milestone !== null,
        );
    },
    [milestonesByUser],
  );

  const getCelebrationForUser = useCallback(
    (
      userId: string,
      date: Date = new Date(),
    ): EmployeeCelebration | null => {
      const resolved = getMilestonesForUser(userId, date);

      if (resolved.length === 0) return null;

      const birthday = resolved.find(
        (milestone) => milestone.type === "BIRTHDAY",
      );
      const anniversary = resolved.find(
        (milestone) => milestone.type === "WORK_ANNIVERSARY",
      );

      const person = birthday ?? anniversary;
      if (!person) return null;

      const name = person.displayName || "This employee";

      if (birthday && anniversary) {
        const years = anniversary.years ?? 0;

        return {
          userId,
          displayName: name,
          birthday,
          anniversary,
          primaryType: "MULTIPLE",
          compactLabel: `Birthday + ${years}-yr anniversary`,
          fullLabel: `It's ${name}'s birthday and ${years}-year work anniversary!`,
          accessibleLabel: `${name} is celebrating a birthday and ${years}-year work anniversary today.`,
        };
      }

      if (birthday) {
        return {
          userId,
          displayName: name,
          birthday,
          primaryType: "BIRTHDAY",
          compactLabel: "Birthday today",
          fullLabel: `It's ${name}'s birthday today!`,
          accessibleLabel: `${name} is celebrating a birthday today.`,
        };
      }

      const years = anniversary?.years ?? 0;

      return {
        userId,
        displayName: name,
        anniversary,
        primaryType: "WORK_ANNIVERSARY",
        compactLabel: `${years}-year anniversary`,
        fullLabel: `Celebrating ${name}'s ${years}-year work anniversary!`,
        accessibleLabel: `${name} is celebrating a ${years}-year work anniversary today.`,
      };
    },
    [getMilestonesForUser],
  );

  const value = useMemo<EmployeeMilestoneContextType>(
    () => ({
      milestones,
      isLoading,
      error,
      refreshMilestones,
      getMilestonesForUser,
      getCelebrationForUser,
    }),
    [
      milestones,
      isLoading,
      error,
      refreshMilestones,
      getMilestonesForUser,
      getCelebrationForUser,
    ],
  );

  return (
    <EmployeeMilestoneContext.Provider value={value}>
      {children}
    </EmployeeMilestoneContext.Provider>
  );
};

export const useEmployeeMilestones = (): EmployeeMilestoneContextType => {
  const context = useContext(EmployeeMilestoneContext);

  if (!context) {
    throw new Error(
      "useEmployeeMilestones must be used inside EmployeeMilestoneProvider",
    );
  }

  return context;
};
